import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const InfoSlider = () => {
  const { currentTenant } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar sliders desde la API
  useEffect(() => {
    const loadSliders = async () => {
      try {
        setLoading(true);
        const data = await apiService.getSliders();
        setSlides(data || []);
      } catch (error) {
        console.error('Error cargando sliders:', error);
        setSlides([]);
      } finally {
        setLoading(false);
      }
    };

    loadSliders();
  }, [currentTenant]);

  // Mapear sliders a formato con iconos
  const dynamicSlides = slides.map(slideConfig => {
    const IconComponent = slideConfig.icon ? LucideIcons[slideConfig.icon] : null;

    return {
      ...slideConfig,
      IconComponent: IconComponent,
    };
  });

  useEffect(() => {
    if (dynamicSlides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % dynamicSlides.length);
    }, 5000); // Cambia cada 5 segundos
    return () => clearInterval(interval);
  }, [dynamicSlides.length]);

  // Si está cargando o no hay slides, no mostrar nada
  if (loading || dynamicSlides.length === 0) {
    return null;
  }

  const variants = {
    enter: { opacity: 0, x: 100 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 },
  };

  const SlideContent = ({ slide }) => (
    <motion.div
      className="flex items-center gap-4 p-4 bg-white/70 backdrop-blur-md rounded-2xl shadow-md border border-white/20"
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {slide.image_url ? (
        <img src={slide.image_url} alt={slide.title} className="w-12 h-12 rounded-xl object-cover" />
      ) : (
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-r ${slide.color}`}>
          {slide.IconComponent && <slide.IconComponent className="w-6 h-6 text-white" />}
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-gray-600">{slide.title}</p>
        <h4 className="text-lg font-bold text-gray-900">{slide.value}</h4>
      </div>
    </motion.div>
  );

  return (
    <div className="relative w-full max-w-md mx-auto lg:mx-0">
      <AnimatePresence mode="wait">
        {dynamicSlides.length > 0 && (
          <SlideContent key={currentIndex} slide={dynamicSlides[currentIndex]} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InfoSlider;